import { useEffect, useState } from 'react'
import { Modal, Button, Field, TextInput, Select, FormGrid } from '@/components/ui'
import { useStore } from '@/store/useStore'
import type { Property, RelationshipType, StabilizationSubState } from '@/types/entities'

const RELATIONSHIPS: RelationshipType[] = ['Owned', 'Managed-3PM', 'JV', 'Development']
const STABILIZATION: StabilizationSubState[] = ['Lease-Up', 'Stabilized']

// Only the key-info fields the API accepts for properties (entities.ts
// PROPERTY_WRITABLE_FIELDS). Identity fields — name, address, codes — are
// sync-owned and deliberately absent.
interface Draft {
  ownerAccountId: string
  fundId: string
  managingEntity: string
  relationshipType: RelationshipType
  stabilization: string
  onboardedDate: string
  stabilizedDate: string
}

function toDraft(p: Property): Draft {
  return {
    ownerAccountId: p.ownerAccountId ?? '',
    fundId: p.fundId ?? '',
    managingEntity: p.managingEntity ?? '',
    relationshipType: p.relationshipType,
    stabilization: p.stabilization ?? '',
    onboardedDate: p.onboardedDate ?? '',
    stabilizedDate: p.stabilizedDate ?? '',
  }
}

export function PropertyKeyInfoForm({
  open, onClose, property,
}: { open: boolean; onClose: () => void; property: Property }) {
  const accounts = useStore((s) => s.data.accounts)
  const funds = useStore((s) => s.data.funds)
  const patchStore = useStore((s) => s.patch)

  const [initial, setInitial] = useState<Draft>(() => toDraft(property))
  const [d, setD] = useState<Draft>(initial)
  // Re-seed both drafts on open so the dirty-diff always compares against
  // what the record held when the form appeared.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { const t = toDraft(property); setInitial(t); setD(t) } }, [open, property.id])
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => ({ ...p, [k]: v }))

  // Attachment targets are LIVE records only: every served account is active,
  // and archived ones are excluded here (the API enforces the same rule).
  const accountOptions = accounts
    .filter((a) => !a.archived)
    .sort((a, b) => a.name.localeCompare(b.name))
  const fundOptions = [...funds].sort((a, b) => a.name.localeCompare(b.name))
  const ownerUnresolved = d.ownerAccountId !== '' && !accountOptions.some((a) => a.id === d.ownerAccountId)

  const dirty = (Object.keys(d) as (keyof Draft)[]).some((k) => d[k] !== initial[k])

  function save() {
    // Dirty-diff PATCH: only changed fields are sent, so an untouched value
    // (including a legacy unresolvable owner id) is never re-validated or
    // silently rewritten. Empty string clears via null.
    const patch: Record<string, unknown> = {}
    if (d.ownerAccountId !== initial.ownerAccountId) patch.ownerAccountId = d.ownerAccountId || null
    if (d.fundId !== initial.fundId) patch.fundId = d.fundId || null
    if (d.managingEntity !== initial.managingEntity) patch.managingEntity = d.managingEntity.trim() || null
    if (d.relationshipType !== initial.relationshipType) patch.relationshipType = d.relationshipType
    if (d.stabilization !== initial.stabilization) patch.stabilization = d.stabilization || null
    if (d.onboardedDate !== initial.onboardedDate) patch.onboardedDate = d.onboardedDate || null
    if (d.stabilizedDate !== initial.stabilizedDate) patch.stabilizedDate = d.stabilizedDate || null
    if (Object.keys(patch).length === 0) { onClose(); return }
    patchStore('properties', property.id, patch as Partial<Property>)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={560}
      title={<>Edit key info — <b>{property.name}</b></>}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={!dirty}>Save changes</Button>
      </>}
    >
      <FormGrid>
        <Field label="Owner Account" span={2}>
          <Select value={d.ownerAccountId} onChange={(e) => set('ownerAccountId', e.target.value)}>
            <option value="">— None —</option>
            {ownerUnresolved && <option value={d.ownerAccountId}>(current value — unresolved)</option>}
            {accountOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
        <Field label="Fund" span={2}>
          <Select value={d.fundId} onChange={(e) => set('fundId', e.target.value)}>
            <option value="">— None —</option>
            {fundOptions.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </Select>
        </Field>
        <Field label="Managing Entity" span={2}>
          <TextInput value={d.managingEntity} onChange={(e) => set('managingEntity', e.target.value)} placeholder="e.g. WPI" />
        </Field>
        <Field label="Relationship">
          <Select value={d.relationshipType} onChange={(e) => set('relationshipType', e.target.value as RelationshipType)}>
            {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
        </Field>
        <Field label="Stabilization">
          <Select value={d.stabilization} onChange={(e) => set('stabilization', e.target.value)}>
            <option value="">— Not set —</option>
            {STABILIZATION.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Onboarded">
          <TextInput type="date" value={d.onboardedDate} onChange={(e) => set('onboardedDate', e.target.value)} />
        </Field>
        <Field label="Stabilized (date)">
          <TextInput type="date" value={d.stabilizedDate} onChange={(e) => set('stabilizedDate', e.target.value)} />
        </Field>
      </FormGrid>
    </Modal>
  )
}
